// drillGen.js — Phase 4: Weakness-aware quiz generator.
//
// Pure helpers for:
//   1. computing per-domain weakness from objective-test history
//   2. picking the bottom 1–2 domains to target
//   3. sampling style-anchor questions from the existing practice bank
//   4. building the generator prompt (system + user)
//   5. parsing and validating the model's JSON response

/**
 * Given an ObjectiveTests history blob and a hub config, return a list of
 * per-domain weakness signals sorted weakest-first.
 *
 *   history: { D1: { best, lastPct, attempts }, ... }
 *   returns: [{ domain, title, status, score, attempts, best, lastPct }]
 *
 * Status priority for sorting:
 *   - 'untested'  (no attempts) — score = -1 (weakest)
 *   - 'failing'   (best < passPct) — score = best
 *   - 'borderline'(passPct..passPct+5) — score = best
 *   - 'strong'    (best > passPct+5) — score = best
 */
export function computeWeakDomains(config, history = {}) {
  const passPct = config.objective?.passPct ?? 75;
  const tests = config.data?.objective || {};
  const out = [];
  Object.keys(tests).forEach((domain) => {
    const h = history[domain] || { best: 0, lastPct: 0, attempts: 0 };
    let status;
    if (!h.attempts) status = 'untested';
    else if (h.best < passPct) status = 'failing';
    else if (h.best < passPct + 5) status = 'borderline';
    else status = 'strong';
    out.push({
      domain,
      title: tests[domain].title || config.domainNames?.[domain] || domain,
      status,
      score: h.attempts ? h.best : -1,
      attempts: h.attempts,
      best: h.best,
      lastPct: h.lastPct,
    });
  });
  // Sort: untested first, then by best ascending
  out.sort((a, b) => a.score - b.score);
  return out;
}

/** Return up to maxN target domain keys (weakest first). */
export function pickTargetDomains(weakList, maxN = 2) {
  if (!weakList.length) return [];
  // If everything is strong, pick the two weakest anyway
  return weakList.slice(0, maxN).map((w) => w.domain);
}

/**
 * Sample n questions from the practice bank for the given domains.
 * Used as style/voice anchors in the generator prompt — NOT as content
 * to copy. Returns trimmed question stems only.
 */
export function samplePracticeAnchors(config, domains, n = 2) {
  const bank = config.data?.practice || [];
  const pool = bank.filter((q) => domains.includes(q.domain));
  if (!pool.length) return [];
  // Shuffle pool, take first n
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n).map((q) => ({
    domain: q.domain,
    stem: q.q,
    sampleOptions: q.options.slice(0, 4),
  }));
}

/**
 * Build the system + user prompt for the drill generator.
 *
 * Strictly enforces JSON output. Returns: { system, prompt }
 */
export function buildPrompt(config, targetDomains, anchors, count = 10) {
  const certName = config.fullTitle || config.title || 'this certification';
  const certCode = config.code ? ` (${config.code})` : '';
  const domainLines = targetDomains
    .map((d) => `- ${d}: ${config.domainNames?.[d] || config.data?.objective?.[d]?.title || d}`)
    .join('\n');
  const anchorsBlock = anchors.length
    ? anchors
        .map(
          (a, i) =>
            `Sample ${i + 1} (${a.domain}):\nQ: ${a.stem}\nA: ${a.sampleOptions
              .map((o, k) => `  ${String.fromCharCode(65 + k)}. ${o}`)
              .join('\n')}`
        )
        .join('\n\n')
    : '(No samples available — use realistic vendor-style scenarios.)';

  const system = `You are an exam-question writer for the ${certName}${certCode} certification.
Generate exactly ${count} multiple-choice questions targeting the user's weakest domains.

STRICT REQUIREMENTS:
- Output ONLY valid JSON. No prose, no markdown, no code fences.
- Match the voice and difficulty of the sample questions provided.
- Each question must be vendor-neutral and answerable in under 90 seconds.
- Each question has exactly 4 options. Exactly one is correct.
- "answer" is the zero-based index (0–3) of the correct option.
- "explanation" must be 1–2 sentences and explain WHY the answer is correct.
- Distribute the ${count} questions across the target domains; do not skip any target domain.
- Do NOT copy or paraphrase the sample questions — they are voice anchors only.

OUTPUT SCHEMA (exact shape):
{
  "questions": [
    {
      "domain": "D1",
      "q": "Question stem...",
      "options": ["A...", "B...", "C...", "D..."],
      "correct": 0,
      "explanation": "Why this is correct."
    }
  ]
}`;

  const prompt = `Target domains for this drill:
${domainLines}

Voice anchors (style and difficulty reference — DO NOT copy):
${anchorsBlock}

Generate ${count} fresh questions now. Output JSON only.`;

  return { system, prompt };
}

/**
 * Parse and validate a model response. Returns an array of questions
 * normalized into ExamRunner's expected shape, or throws with a clear
 * error message.
 *
 *   { id, domain, q, options[4], correct, explanation }
 */
export function parseDrillJSON(text, targetDomains) {
  if (!text || typeof text !== 'string') {
    throw new Error('Empty response from AI.');
  }
  // Strip optional code fences (some models ignore "no markdown")
  let cleaned = text.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/, '');
  }
  // Find the first { and last } in case of leading/trailing prose
  const firstBrace = cleaned.indexOf('{');
  const lastBrace = cleaned.lastIndexOf('}');
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
    throw new Error('Response did not contain a JSON object.');
  }
  const jsonStr = cleaned.slice(firstBrace, lastBrace + 1);

  let parsed;
  try {
    parsed = JSON.parse(jsonStr);
  } catch (e) {
    throw new Error(`Could not parse JSON: ${e.message}`);
  }

  const rawQs = Array.isArray(parsed) ? parsed : parsed.questions;
  if (!Array.isArray(rawQs) || !rawQs.length) {
    throw new Error('Response did not contain a non-empty questions array.');
  }

  const targetSet = new Set(targetDomains);
  const ts = Date.now();
  const normalized = rawQs.map((raw, i) => {
    const stem = typeof raw.q === 'string' ? raw.q.trim() : typeof raw.stem === 'string' ? raw.stem.trim() : '';
    const options = Array.isArray(raw.options) ? raw.options.map((o) => String(o).trim()) : [];
    const correct = Number.isInteger(raw.correct) ? raw.correct : Number.isInteger(raw.answer) ? raw.answer : -1;
    const explanation = typeof raw.explanation === 'string' ? raw.explanation.trim() : '';
    let domain = typeof raw.domain === 'string' ? raw.domain.trim() : '';
    if (!targetSet.has(domain)) {
      // Pin to first target domain if model used an unknown label
      domain = targetDomains[0];
    }
    if (!stem) throw new Error(`Question #${i + 1} missing stem.`);
    if (options.length !== 4) throw new Error(`Question #${i + 1} must have exactly 4 options (got ${options.length}).`);
    if (correct < 0 || correct > 3) throw new Error(`Question #${i + 1} has invalid correct index (${correct}).`);
    if (!explanation) throw new Error(`Question #${i + 1} missing explanation.`);
    return {
      id: `drill-${ts}-${i}`,
      domain,
      q: stem,
      options,
      correct,
      explanation,
    };
  });

  return normalized;
}

/** Human label for a weakness status. */
export function statusLabel(status) {
  return {
    untested: 'UNTESTED',
    failing: 'BELOW PASS',
    borderline: 'BORDERLINE',
    strong: 'STRONG',
  }[status] || status.toUpperCase();
}
